import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { getViewerTimezone, formatInstantForViewer } from '../utils/dateTime';
import './UserActivityPage.css';

const ACTIVE_STATUSES = ['ACTIVE', 'UNDER_CONTRACT', 'CLOSING'];
const BROKER_ROLES    = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];

// ── Signed-in user's db id — used to render "ME" on their own actions ───────
// Mirrors mobile AuditEventRow's `isSelf` check (actorUserId === user.id).
// Reads the same localStorage.user blob every other page uses. Returns null
// for a logged-out viewer, which collapses the self-detection branch and the
// actor tag just renders the enriched actorName/actorRole directly.
function readSelfUserId() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return typeof stored.id === 'string' ? stored.id : null;
  } catch {
    return null;
  }
}

// Title-case a role enum ("BUYER" → "Buyer") for display. Mirrors mobile
// prettyRole in AuditEventRow.tsx so both platforms show the same label.
function prettyRole(role) {
  if (!role || typeof role !== 'string') return null;
  return role.charAt(0) + role.slice(1).toLowerCase();
}

// Returns the "by whom" label for one event, mirroring mobile's actorTagText:
//   • viewer === actor         → "ME"
//   • actorName present        → "Name · Role" or just "Name"
//   • only role present        → "Role"
//   • nothing available        → null (caller renders em-dash)
// Backend's AuditMetadataEnricher populates actorName + actorRole on every
// transaction-scoped event; older rows written before that enricher shipped
// may be missing both and fall through to null.
function getActorLabel(event, selfUserId) {
  if (!event) return null;
  if (selfUserId && event.actorUserId && event.actorUserId === selfUserId) {
    return 'ME';
  }
  const meta = event.metadata || {};
  const actorName = typeof meta.actorName === 'string' && meta.actorName.length > 0
    ? meta.actorName : null;
  const role = prettyRole(typeof meta.actorRole === 'string' ? meta.actorRole : null);
  if (actorName) return role ? `${actorName} · ${role}` : actorName;
  if (role) return role;
  return null;
}

// ── Subtitle resolver — extracts context from event.metadata ─────────────────
function getEventSubtitle(event) {
  const meta = event.metadata || {};
  const str  = (key) => (typeof meta[key] === 'string' && meta[key].length > 0 ? meta[key] : null);
  const arr  = (key) => (Array.isArray(meta[key]) ? meta[key] : null);

  switch (event.eventType) {
    case 'TRANSACTION_MILESTONE_CREATED':
    case 'TRANSACTION_MILESTONE_UPDATED':
    case 'TRANSACTION_MILESTONE_COMPLETED':
      return str('milestoneName');

    case 'TRANSACTION_CONTINGENCY_ADDED':
    case 'TRANSACTION_CONTINGENCY_UPDATED':
    case 'TRANSACTION_CONTINGENCY_SATISFIED':
    case 'TRANSACTION_CONTINGENCY_WAIVED':
    case 'TRANSACTION_CONTINGENCY_REMOVED':
      return str('contingencyName');

    case 'TRANSACTION_TASK_CREATED':
    case 'TRANSACTION_TASK_UPDATED':
    case 'TRANSACTION_TASK_COMPLETED':
    case 'TRANSACTION_TASK_CANCELLED':
      return str('taskTitle');

    case 'TRANSACTION_PARTY_ADDED':
      return str('role');

    case 'TRANSACTION_PARTY_UPDATED':
    case 'TRANSACTION_PARTY_REMOVED':
      return str('participantRole');

    case 'TRANSACTION_STATUS_CHANGED': {
      const from = str('fromStatus');
      const to   = str('toStatus');
      if (from && to) return `${from} → ${to}`;
      return null;
    }

    case 'TRANSACTION_UPDATED': {
      const stages = arr('prospectStageChanges');
      if (stages && stages.length > 0) {
        const s = stages[0];
        const toStatus = s.toStatus
          ? s.toStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          : '';
        return `Stage: ${s.stageName} → ${toStatus}`;
      }
      const changed = arr('changedFields');
      if (!changed || changed.length === 0) return null;
      if (changed.length === 1) return `Changed ${changed[0]}`;
      return `${changed.length} fields changed`;
    }

    case 'DOCUMENT_UPLOADED':
    case 'DOCUMENT_DELETED':
      return str('fileName');

    case 'OFFER_SUBMITTED_VIA_LINK':
    case 'OFFER_DOCUMENTS_ADDED_VIA_LINK': {
      // Bulk upload via the public Offer link — tx-service enriches the
      // audit event with a fileNames array (plus a numeric fileCount
      // string). Prefer the full name list; fall back to the count-only
      // phrase on older rows that only carry fileCount.
      const names = arr('fileNames');
      if (names && names.length > 0) {
        if (names.length === 1) return names[0];
        if (names.length <= 3) return names.join(', ');
        return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
      }
      const count = str('fileCount');
      if (count) return `${count} ${count === '1' ? 'document' : 'documents'}`;
      return null;
    }

    case 'SERVICE_REQUEST_CREATED':
      return str('serviceType');

    case 'CALENDAR_EVENT_CREATED':
      return str('title');

    default:
      return null;
  }
}

// ── Timestamp helpers ─────────────────────────────────────────────────────────
// Renders in the viewer's own profile timezone (via formatInstantForViewer),
// not the device's timezone — same fix as the calendar/service-request entry
// points, applied here to the Activity Log's absolute-time display.
function formatTimestamp(isoString, viewerTimezone = getViewerTimezone()) {
  if (!isoString) return '—';
  const now = new Date();
  const { dateLabel, timeLabel } = formatInstantForViewer(isoString, viewerTimezone);
  // "Today" is a real calendar-day comparison in the viewer's zone, not the
  // device's — the two can disagree near a day boundary.
  const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: viewerTimezone });
  if (dayKey(new Date(isoString)) === dayKey(now)) return `Today · ${timeLabel}`;
  // dateLabel is "Mon D, YYYY" — drop the year to match this page's existing short style.
  const shortDate = dateLabel.replace(/, \d{4}$/, '');
  return `${shortDate} · ${timeLabel}`;
}

function getRelativeTime(isoString) {
  if (!isoString) return '—';
  const ts   = new Date(isoString);
  const now  = new Date();
  const diff = now - ts;
  const min  = Math.floor(diff / 60000);
  const hr   = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (diff < 60000)  return 'Just now';
  if (min  < 60)     return `${min}m ago`;
  if (hr   < 24)     return `${hr}h ago`;
  if (days === 1)    return 'Yesterday';
  if (days < 7)      return `${days}d ago`;
  return ts.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    timeZone: getViewerTimezone(),
  });
}

// ── Category → backend values used by the audit API ──────────────────────────
const CATEGORY_BACKEND_MAP = {
  TRANSACTION:  ['TRANSACTION'],
  AUTH:         ['AUTH', 'USER'],
  PAYMENT:      ['PAYMENT'],
  DOCUMENT:     ['FILE', 'DOCUSIGN'],
  VERIFICATION: ['VERIFICATION'],
};

const CATEGORY_PILLS = [
  { key: '',             label: 'All'          },
  { key: 'TRANSACTION',  label: 'Transaction'  },
  { key: 'AUTH',         label: 'Auth & Access'},
  { key: 'PAYMENT',      label: 'Payment'      },
  { key: 'DOCUMENT',     label: 'Document'     },
  { key: 'VERIFICATION', label: 'Verification' },
];

// ── Verb → type badge helpers (mirrors mobile AuditEventRow) ─────────────────
function verbBadgeType(verb) {
  if (verb === 'Created') return 'create';
  if (verb === 'Updated') return 'update';
  if (verb === 'Removed') return 'delete';
  return 'other';
}

function verbBadgeLabel(verb) {
  if (verb === 'Created') return 'CREATE';
  if (verb === 'Updated') return 'UPDATE';
  if (verb === 'Removed') return 'DELETE';
  return 'OTHER';
}

// ── Entity tag from eventType (mirrors mobile entityTagFromEventType) ─────────
function entityTagFromEventType(eventType) {
  if (!eventType) return null;
  const stripped = eventType.startsWith('TRANSACTION_')
    ? eventType.slice('TRANSACTION_'.length)
    : eventType;

  if (stripped.startsWith('MILESTONE'))   return 'Milestone';
  if (stripped.startsWith('CONTINGENCY')) return 'Contingency';
  if (stripped.startsWith('TASK'))        return 'Task';
  if (stripped.startsWith('PARTY'))       return 'Party';
  if (stripped.startsWith('NOTE'))        return 'Note';
  if (stripped.startsWith('DOCUMENT') || stripped.startsWith('FILE')) return 'Document';
  if (stripped.startsWith('DOCUSIGN'))    return 'Document';
  if (stripped.startsWith('PAYMENT'))     return 'Payment';
  if (stripped.startsWith('LICENSE'))     return 'License';
  if (['CREATED', 'UPDATED', 'DELETED', 'STATUS_CHANGED', 'KEY_CREATED'].includes(stripped)) return 'Transaction';
  if (eventType === 'SERVICE_REQUEST_CREATED') return 'Service Request';
  if (eventType === 'CALENDAR_EVENT_CREATED')  return 'Calendar Event';
  return null;
}

// ── Severity badge CSS class ──────────────────────────────────────────────────
function severityBadgeClass(sev) {
  switch (sev) {
    case 'WARN':     return 'uap-badge--warn';
    case 'ERROR':    return 'uap-badge--error';
    case 'CRITICAL': return 'uap-badge--critical';
    default:         return 'uap-badge--info';
  }
}

// ── Category label override for entity-specific event types ──────────────────
// SERVICE_REQUEST_CREATED and CALENDAR_EVENT_CREATED are cataloged under the
// shared TRANSACTION uiCategory bucket server-side (EventDisplayCatalog,
// audit-context 1.3.0+) so they stay under the existing "Transaction" filter
// pill — but showing the literal word "TRANSACTION" on these rows reads as
// wrong to users. This overrides only the displayed badge text/icon; it does
// not change event.category, so filtering behavior is untouched.
function displayCategory(event) {
  if (event.eventType === 'CALENDAR_EVENT_CREATED') return 'CALENDAR';
  if (event.eventType === 'SERVICE_REQUEST_CREATED') return 'SERVICE';
  return event.display?.uiCategory || event.category || '';
}

// ── Category icon (SVG) ───────────────────────────────────────────────────────
function CategoryIcon({ category }) {
  switch (category) {
    case 'CALENDAR':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      );
    case 'SERVICE':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
        </svg>
      );
    case 'TRANSACTION':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      );
    case 'AUTH':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      );
    case 'PAYMENT':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      );
    case 'DOCUMENT':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      );
    case 'VERIFICATION':
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <polyline points="9 12 11 14 15 10"/>
        </svg>
      );
    default:
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      );
  }
}

const PAGE_SIZE = 60;

const DEFAULT_TO   = new Date().toISOString().slice(0, 10);
const DEFAULT_FROM = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
})();

// ── Main page ─────────────────────────────────────────────────────────────────
const UserActivityPage = () => {
  const navigate = useNavigate();

  // Signed-in user's own db id — captured once at mount so getActorLabel can
  // flip the actor tag to "ME" on the viewer's own actions (same contract as
  // mobile AuditEventRow's `isSelf` check).
  const selfUserId = React.useMemo(readSelfUserId, []);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (localStorage.getItem('isAuthenticated') !== 'true') {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // ── Firebase token helper ──────────────────────────────────────────────────
  const getFirebaseToken = useCallback(() => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const tid = setTimeout(() => { unsub(); reject(new Error('Auth timeout')); }, 10000);
    const unsub = auth.onAuthStateChanged(async (user) => {
      clearTimeout(tid);
      unsub();
      if (user) {
        try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
      } else {
        reject(new Error('Not authenticated'));
      }
    });
  }), []);

  // ── Audit API helper — serialises repeated `category` params ──────────────
  const getAuditEvents = useCallback(async (query, signal) => {
    const token  = await getFirebaseToken();
    const params = {};
    if (query.pageSize)         params.pageSize = query.pageSize;
    if (query.cursor)           params.cursor   = query.cursor;
    if (query.from)             params.from     = query.from;
    if (query.to)               params.to       = query.to;
    if (query.category?.length) params.category = query.category;
    if (query.verb?.length)     params.verb     = query.verb;

    const response = await apiClient.get(API_ENDPOINTS.auditUserEvents, {
      headers: { Authorization: `Bearer ${token}` },
      params,
      signal,
      paramsSerializer: (p) => {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(p)) {
          if (Array.isArray(value)) {
            value.forEach(v => sp.append(key, String(v)));
          } else if (value !== undefined && value !== null) {
            sp.append(key, String(value));
          }
        }
        return sp.toString();
      },
    });
    return response.data; // { events, nextCursor, hasMore }
  }, [getFirebaseToken]);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [category,     setCategory]     = useState('');
  const [verbs,        setVerbs]        = useState([]);
  const [filterFrom,   setFilterFrom]   = useState(DEFAULT_FROM);
  const [filterTo,     setFilterTo]     = useState(DEFAULT_TO);
  const [appliedFrom,  setAppliedFrom]  = useState(DEFAULT_FROM);
  const [appliedTo,    setAppliedTo]    = useState(DEFAULT_TO);

  // ── Events + pagination state ───────────────────────────────────────────────
  const [events,      setEvents]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor,  setNextCursor]  = useState(null);
  const [hasMore,     setHasMore]     = useState(false);
  const [error,       setError]       = useState(null);

  // ── Active transaction count (fetched once on mount) ──────────────────────
  // Mirrors mobile AuditStatsCards:
  //   Broker roles  → sum linked agents' activeCount via /broker/transactions/counts
  //   All other roles → /transactions filtered by ACTIVE+UNDER_CONTRACT+CLOSING + viewingRole
  const [activeTransactionCount, setActiveTransactionCount] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token      = await getFirebaseToken();
        const activeRole = (getActiveRoleFromStorage() || '').toUpperCase();
        const headers    = { Authorization: `Bearer ${token}` };
        let count;

        if (BROKER_ROLES.includes(activeRole)) {
          // Broker: sum activeCount across all linked agents (mirrors mobile getAgentTransactionCounts)
          const agentsRes = await apiClient.get(API_ENDPOINTS.brokerAgents, { headers });
          const raw       = agentsRes.data;
          const agents    = Array.isArray(raw) ? raw
            : Array.isArray(raw?.data) ? raw.data
            : Array.isArray(raw?.content) ? raw.content
            : [];
          const agentIds  = agents.map(a => a.userId).filter(Boolean);
          if (agentIds.length > 0) {
            const params = new URLSearchParams();
            agentIds.forEach(id => params.append('agentUserIds', id));
            const cRes  = await apiClient.get(
              `${API_ENDPOINTS.brokerTransactionCounts}?${params.toString()}`, { headers }
            );
            const cData = Array.isArray(cRes.data) ? cRes.data
              : Array.isArray(cRes.data?.data) ? cRes.data.data
              : [];
            count = cData.reduce((sum, c) => sum + (c.activeCount || 0), 0);
          } else {
            count = 0;
          }
        } else {
          // All other roles: query with viewingRole + all three active statuses
          const res = await apiClient.get(API_ENDPOINTS.transactions, {
            headers,
            params: {
              status:      ACTIVE_STATUSES.join(','),
              viewingRole: activeRole || undefined,
              size:        1,
            },
          });
          const total = res.data?.totalElements ?? res.data?.total ?? null;
          count = total !== null ? total : '--';
        }

        setActiveTransactionCount(count);
      } catch {
        setActiveTransactionCount('--');
      }
    })();
  }, [getFirebaseToken]);

  // ── Fetch events ─────────────────────────────────────────────────────────────
  const abortRef = useRef(null);

  const fetchEvents = useCallback(async (cursor) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isFirst = !cursor;
    if (isFirst) { setLoading(true); setEvents([]); }
    else         { setLoadingMore(true); }
    setError(null);

    try {
      const backendCategories = category ? CATEGORY_BACKEND_MAP[category] : undefined;
      const result = await getAuditEvents({
        pageSize: PAGE_SIZE,
        cursor:   cursor || undefined,
        category: backendCategories,
        verb:     verbs.length > 0 ? verbs : undefined,
        from:     appliedFrom ? `${appliedFrom}T00:00:00.000Z` : undefined,
        to:       appliedTo   ? `${appliedTo}T23:59:59.999Z`   : undefined,
      }, controller.signal);

      const visible = (result.events || []).filter(e => !e.display?.hidden);
      setEvents(prev => isFirst ? visible : [...prev, ...visible]);
      setNextCursor(result.nextCursor || null);
      setHasMore(!!result.hasMore);
    } catch (err) {
      if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
        setError('Failed to load activity. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, verbs, appliedFrom, appliedTo, getAuditEvents]);

  useEffect(() => {
    fetchEvents(null);
  }, [fetchEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) fetchEvents(nextCursor);
  };

  // ── Filter handlers ──────────────────────────────────────────────────────────
  const handleCategoryChange = (val) => {
    setCategory(val);
    if (val === 'AUTH') setVerbs([]);
  };

  const handleVerbToggle = (verb) => {
    setVerbs(prev => prev.includes(verb) ? prev.filter(v => v !== verb) : [...prev, verb]);
  };

  const handleApplyDates = () => {
    setAppliedFrom(filterFrom);
    setAppliedTo(filterTo);
  };

  const hasActiveFilters = category !== '' || verbs.length > 0 || appliedFrom !== DEFAULT_FROM || appliedTo !== DEFAULT_TO;

  const handleReset = () => {
    setCategory('');
    setVerbs([]);
    setFilterFrom(DEFAULT_FROM);
    setFilterTo(DEFAULT_TO);
    setAppliedFrom(DEFAULT_FROM);
    setAppliedTo(DEFAULT_TO);
  };

  // ── Export CSV ───────────────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    try {
      const backendCategories = category ? CATEGORY_BACKEND_MAP[category] : undefined;
      const result = await getAuditEvents({
        pageSize: 10000,
        category: backendCategories,
        verb:     verbs.length > 0 ? verbs : undefined,
        from:     appliedFrom ? `${appliedFrom}T00:00:00.000Z` : undefined,
        to:       appliedTo   ? `${appliedTo}T23:59:59.999Z`   : undefined,
      });
      const visible = (result.events || []).filter(e => !e.display?.hidden);
      const header  = ['Timestamp', 'Category', 'Action', 'Details', 'Severity'];
      const csvRows = [
        header.join(','),
        ...visible.map(r => [
          `"${formatTimestamp(r.timestamp)}"`,
          `"${displayCategory(r)}"`,
          `"${r.display?.label      || r.eventType || ''}"`,
          `"${getEventSubtitle(r)   || ''}"`,
          `"${r.severity            || ''}"`,
        ].join(','))
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `my-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ── Stats derivation ─────────────────────────────────────────────────────────
  const firstEvent   = events[0];
  const lastRelative = firstEvent ? getRelativeTime(firstEvent.timestamp) : '—';
  const lastTime     = firstEvent ? formatTimestamp(firstEvent.timestamp) : '';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="uap-page">
      <div className="uap-main-content">
        <div className="uap-content-box">
          <div className="uap-content">

            {/* ── Page Header ── */}
            <div className="uap-page-header">
              <button className="uap-back-btn" onClick={() => navigate(-1)}>
                <span>←</span>
                <span>Back</span>
              </button>
              <div className="uap-header-row">
                <div>
                  <h1 className="uap-page-title">My Activity</h1>
                  <p className="uap-page-subtitle">Your personal audit history</p>
                </div>
                <button className="uap-export-btn" onClick={handleExportCSV}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
              </div>
              <div className="uap-scope-note">Showing your personal activity only</div>
            </div>

            {/* ── Stats ── */}
            <div className="uap-stats">
              <div className="uap-stat">
                <div className="uap-stat-label">My Transactions</div>
                <div className="uap-stat-val">
                  {activeTransactionCount === null
                    ? <span className="uap-skeleton-val" />
                    : activeTransactionCount}
                </div>
                <div className="uap-stat-sub">Active</div>
              </div>
              <div className="uap-stat">
                <div className="uap-stat-label">Actions</div>
                <div className="uap-stat-val">
                  {loading ? <span className="uap-skeleton-val" /> : events.length}
                </div>
                <div className="uap-stat-sub">This view</div>
              </div>
              <div className="uap-stat">
                <div className="uap-stat-label">Last Activity</div>
                <div className="uap-stat-val uap-stat-val--sm">
                  {loading ? <span className="uap-skeleton-val uap-skeleton-val--sm" /> : lastRelative}
                </div>
                <div className="uap-stat-sub">{loading ? '' : (lastTime || 'No events')}</div>
              </div>
            </div>

            {/* ── Category Pills ── */}
            <div className="uap-pills-row">
              {CATEGORY_PILLS.map(pill => (
                <button
                  key={pill.key}
                  className={`uap-pill${category === pill.key ? ' uap-pill--active' : ''}`}
                  onClick={() => handleCategoryChange(pill.key)}
                >{pill.label}</button>
              ))}
            </div>

            {/* ── Filters ── */}
            <div className="uap-filters">
              {category !== 'AUTH' && (
                <div className="uap-filter-group">
                  <label className="uap-filter-label">Action</label>
                  <div className="uap-verb-wrap">
                    {['Created', 'Updated', 'Removed'].map(v => (
                      <button
                        key={v}
                        className={`uap-verb-pill${verbs.includes(v) ? ' uap-verb-pill--active' : ''}`}
                        onClick={() => handleVerbToggle(v)}
                      >{v}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="uap-filter-group">
                <label className="uap-filter-label">From</label>
                <input
                  className="uap-filter-input"
                  type="date"
                  value={filterFrom}
                  max={filterTo || undefined}
                  onChange={e => setFilterFrom(e.target.value)}
                />
              </div>
              <div className="uap-filter-group">
                <label className="uap-filter-label">To</label>
                <input
                  className="uap-filter-input"
                  type="date"
                  value={filterTo}
                  min={filterFrom || undefined}
                  onChange={e => setFilterTo(e.target.value)}
                />
              </div>
              <button className="uap-apply-btn" onClick={handleApplyDates}>Apply</button>
              {hasActiveFilters && (
                <button className="uap-reset-btn" onClick={handleReset}>Reset</button>
              )}
            </div>

            {/* ── Table ── */}
            <div className="uap-table-wrap">
              {error && (
                <div className="uap-error">
                  {error}
                  <button className="uap-retry-btn" onClick={() => fetchEvents(null)}>Retry</button>
                </div>
              )}
              <table className="uap-table">
                <colgroup>
                  <col className="uap-col-ts" />
                  <col className="uap-col-type" />
                  <col className="uap-col-cat" />
                  <col className="uap-col-action" />
                  <col className="uap-col-details" />
                  <col className="uap-col-entity" />
                  <col className="uap-col-actor" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Transaction</th>
                    <th>Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j}><span className="uap-cell-skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  ) : events.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="uap-empty">
                        <div className="uap-empty-icon">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C4BDF0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <line x1="7" y1="8" x2="17" y2="8"/>
                            <line x1="7" y1="12" x2="17" y2="12"/>
                            <line x1="7" y1="16" x2="13" y2="16"/>
                          </svg>
                        </div>
                        <div className="uap-empty-text">No activity found</div>
                        <div className="uap-empty-sub">
                          {hasActiveFilters
                            ? 'Try adjusting your filters'
                            : 'Your activity will appear here as you use Rialty'}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    events.map(event => {
                      const uiCat     = displayCategory(event);
                      const subtitle  = getEventSubtitle(event);
                      const badgeType = verbBadgeType(event.display?.verb);
                      const txnName   = event.metadata?.transactionName || '';
                      const entityTag = entityTagFromEventType(event.eventType);
                      const actionLabel = event.display?.label || event.eventType || '—';
                      const actorLabel = getActorLabel(event, selfUserId);
                      const actorIsSelf = actorLabel === 'ME';
                      return (
                        <tr key={event.eventId}>
                          <td className="uap-td-ts">{formatTimestamp(event.timestamp)}</td>
                          <td>
                            <span className={`uap-type-badge uap-type-badge--${badgeType}`}>
                              {verbBadgeLabel(event.display?.verb)}
                            </span>
                          </td>
                          <td>
                            <span className="uap-cat-tag">
                              <CategoryIcon category={uiCat} />
                              {uiCat}
                            </span>
                          </td>
                          <td className="uap-td-label" title={actionLabel}>
                            {actionLabel}
                          </td>
                          <td className="uap-td-details">
                            <div className="uap-details-wrap">
                              {subtitle
                                ? <span className="uap-context-tag" title={subtitle}>{subtitle}</span>
                                : <span className="uap-td-empty">—</span>}
                            </div>
                          </td>
                          <td>
                            {txnName
                              ? <span className="uap-entity-tag" title={txnName}>{txnName}</span>
                              : <span className="uap-td-empty">—</span>}
                          </td>
                          <td>
                            {actorLabel
                              ? <span
                                  className={actorIsSelf ? 'uap-me-tag' : 'uap-actor-tag'}
                                  title={actorLabel}>
                                  {actorLabel}
                                </span>
                              : <span className="uap-td-empty">—</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {!loading && hasMore && (
                <div className="uap-load-more-wrap">
                  <button
                    className="uap-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore
                      ? <><span className="uap-spinner" /> Loading…</>
                      : 'Load More'}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UserActivityPage;
